import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  signal,
  Signal
} from '@angular/core';
import {AmenityLabelPipe} from "../../pipes/amenity-label.pipe";
import {ApartmentAddressPipe} from "../../pipes/apartment-address.pipe";
import {
  ApartmentDetailInfoItemComponent
} from "../../pages/apartments-tab/apartment-detail-page/apartment-detail-info-item/apartment-detail-info-item.component";
import {SlicePipe, DOCUMENT} from "@angular/common";
import {Button, ButtonDirective} from "primeng/button";
import {Divider} from "primeng/divider";
import {PageSubtitleComponent} from "../page-subtitle/page-subtitle.component";
import {PageTitleComponent} from "../page-title/page-title.component";
import {PrimeTemplate} from "primeng/api";
import {SafePipe} from "../../pipes/safe.pipe";
import {Lottery} from "../../model/lottery";
import {ApplicationWithOutdatedReason} from "../../model/application";
import {definePreset} from "@primeng/themes";
import {MyPreset} from "../../theme";
import {RelevantApartmentsInput} from "../../pipes/apartment-relevant-advertisements.pipe";
import {GalleriaModule} from "primeng/galleria";
import {AmenityIconPipe} from "../../pipes/amenity-icon.pipe";
import {RouterLink} from "@angular/router";
import {initialSignupRoute, initialSignupRouteIncludingInfo} from "../../pages/signup/specific-signup.routes";
import {DollarPipe} from "../../pipes/dollar.pipe";
import {ToNearestDecimalPipe} from "../../pipes/to-nearest-decimal.pipe";
import {MinRentPipe} from "../../pipes/min-rent.pipe";
import {EstimatedSavingsPipe} from "../../pipes/estimated-savings.pipe";
import {MinPipe} from "../../pipes/min.pipe";
import {MaxPipe} from "../../pipes/max.pipe";
import {UserService} from "../../services/user.service";
import {AnalyticsService} from "../../services/analytics.service";
import {ShareService} from "../../services/share.service";
import {TranslocoPipe} from "@jsverse/transloco";
import {TranslateService} from "../../services/translate.service";
import {ImageMode} from "../../utils/image-mode";
import {buildImageUrl, buildSrcSet, getImageSizes} from "../../utils/image-source";
import {HomeyImageDirective} from "../../directives/homey-image.directive";
import {ProgressSpinner} from "primeng/progressspinner";

@Component({
  selector: 'homey-apartment-detail-page-content',
  imports: [
    AmenityLabelPipe,
    ApartmentAddressPipe,
    ApartmentDetailInfoItemComponent,
    Button,
    Divider,
    PageSubtitleComponent,
    PageTitleComponent,
    PrimeTemplate,
    SafePipe,
    SlicePipe,
    GalleriaModule,
    ProgressSpinner,
    AmenityIconPipe,
    ButtonDirective,
    RouterLink,
    DollarPipe,
    ToNearestDecimalPipe,
    MinRentPipe,
    EstimatedSavingsPipe,
    MinPipe,
    MaxPipe,
    TranslocoPipe,
    HomeyImageDirective,
  ],
  templateUrl: './apartment-detail-page-content.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApartmentDetailPageContentComponent implements OnDestroy {
  public readonly apartment = input.required<Lottery>();
  public readonly application = input<ApplicationWithOutdatedReason>();
  public readonly relevantApartmentsInput = input.required<RelevantApartmentsInput>();
  public readonly householdSize = input.required<number>();
  private readonly translateService = inject(TranslateService);
  public readonly showH2 = input<boolean>(false);
  public readonly showEligibilityCarouselCta = input<boolean>(true);
  public readonly modalOpen = signal(false);
  protected readonly description = computed(
    () => this.translateService.language() === 'es' ? this.apartment().esDescription : this.apartment().description
  );
  private readonly homeyShareDate = computed(
    () => (
      {
        title: `${this.apartment().name} - Affordable Housing Lottery`,
        url: `https://www.myhomey.io/apartments/${this.apartment().slug ?? this.apartment().id}?utm_campaign=share`,
      }
    )
  )


  private readonly shareService = inject(ShareService);
  private readonly userService = inject(UserService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly user = this.userService.user;
  private readonly document = inject(DOCUMENT);
  private outsideClickListener: ((event: MouseEvent) => void) | null = null;

  protected readonly canShare = computed(
    () => this.shareService.canShare(
      this.homeyShareDate()
    )
  );

  private readonly purePhotos = computed(
    () => this.apartment().photos.map<{
      last: false,
      alt: string,
      baseImage: string
    }>(photo => ({
      alt: `${this.apartment().name}, ${this.apartment().addressInfo.address}, ${this.apartment().neighborhood}, ${this.apartment().addressInfo.city}, ${this.apartment().addressInfo.state} ${this.apartment().addressInfo.zip}, ${photo.alt ? (' - ' + photo.alt) : ''}`,
      baseImage: photo.baseImage,
      last: false
    }))
  )
  protected readonly photos: Signal<({
    last: false,
    alt: string,
    baseImage: string
  } | {
    last: true
    alt: string,
    baseImage: string
  })[]> = computed(
    () => {
      const purePhotos = this.purePhotos();
      if (this.user()?.status === 'unauthorized' && this.showEligibilityCarouselCta()) {
        return [...purePhotos, {last: true, alt: '', baseImage: purePhotos[0].baseImage}]
      }
      return purePhotos;
    }
  )
  private readonly closingMidday = computed(() => {
    const closing = new Date(this.apartment().endDate);
    closing.setHours(12, 0, 0, 0);
    return closing;
  });
  protected readonly drawingDate = computed(() => this.addDays(new Date(this.apartment().endDate), 21));
  protected readonly drawingDateInPast = computed(() => this.drawingDate() < new Date());
  protected readonly timelineState = computed(() => {
    if (this.apartment().status !== 'open') {
      return {type: 'closed'} as const;
    }

    const now = new Date();
    const closingDate = new Date(this.apartment().endDate);

    if (now >= closingDate) {
      return {type: 'closed'} as const;
    }

    const midday = this.closingMidday();
    if (now >= midday) {
      return {type: 'today'} as const;
    }

    const diffMs = midday.getTime() - now.getTime();
    const hourMs = 1000 * 60 * 60;
    const dayMs = hourMs * 24;

    const totalHours = Math.floor(diffMs / hourMs);
    const days = Math.floor(totalHours / 24);
    const hours = Math.max(0, totalHours - days * 24);
    const includeHours = diffMs <= 2 * dayMs;

    const lessThanHour = includeHours && days === 0 && hours === 0;

    return {
      type: 'time' as const,
      days,
      hours,
      includeHours,
      lessThanHour,
    };
  });

  public showAllAmenities = false;
  protected showAllDescription = false;

  private readonly activeIndexSignal = signal(0);
  protected get activeIndex() {
    return this.activeIndexSignal();
  }
  protected set activeIndex(value: number) {
    this.activeIndexSignal.set(value);
  }

  private readonly preloadedImageKeys = new Set<string>();
  private readonly stopPrefetchEffect = effect(() => {
    this.photos();
    const currentIndex = this.activeIndexSignal();
    const fullOpen = this.modalOpen();
    this.prefetchNextImages(currentIndex, fullOpen);
  });
  private readonly handleOutsideClick = (event: MouseEvent) => {
    if (!(event.target instanceof Element)) {
      return;
    }
    if (event.target.closest('.p-galleria-content')) {
      return;
    }
    this.modalOpen.set(false);
  };

  private readonly stopOutsideClickEffect = effect(() => {
    if (!this.modalOpen()) {
      this.removeOutsideClickListener();
      return;
    }
    if (!this.document || typeof this.document.addEventListener !== 'function' || this.outsideClickListener) {
      return;
    }
    this.outsideClickListener = this.handleOutsideClick;
    this.document.addEventListener('click', this.outsideClickListener);
  });

  addDays(date: Date, days: number) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  toggleShowAmenities() {
    this.showAllAmenities = !this.showAllAmenities;
  }

  toggleShowMore() {
    this.showAllDescription = !this.showAllDescription;
  }

  openGallery() {
    this.modalOpen.set(true);
  }

  ngOnDestroy(): void {
    this.removeOutsideClickListener();
    this.stopPrefetchEffect.destroy();
    this.stopOutsideClickEffect.destroy();
  }

  private prefetchNextImages(currentIndex: number, prefetchFull: boolean) {
    const photos = this.photos();
    const total = photos.length;
    const currentSource = photos[currentIndex]?.baseImage;
    for (let offset = 1; offset <= 3; offset++) {
      const index = currentIndex + offset;
      if (index >= total) {
        break;
      }
      const photo = photos[index];
      const source = photo.baseImage;
      if (!source || source === currentSource) {
        continue;
      }
      const modes: ImageMode[] = ['header'];
      if (prefetchFull) {
        modes.push('full');
      }
      modes.forEach(mode => this.preloadImageVariant(source, mode));
    }
  }

  private preloadImageVariant(source: string, mode: ImageMode) {
    const key = `${source}::${mode}`;
    if (this.preloadedImageKeys.has(key)) {
      return;
    }
    this.preloadedImageKeys.add(key);

    const img = new Image();
    img.decoding = 'async';
    img.fetchPriority = 'low';
    img.srcset = buildSrcSet(source, mode);
    img.sizes = getImageSizes(mode);
    img.src = buildImageUrl(source, mode);
  }

  onShare() {
    this.analyticsService.sendEvent(
      "share-apartment",
    )
    this.shareService.share(
      this.homeyShareDate()
    )
  }

  private removeOutsideClickListener() {
    if (
      this.outsideClickListener &&
      this.document &&
      typeof this.document.removeEventListener === 'function'
    ) {
      this.document.removeEventListener('click', this.outsideClickListener);
      this.outsideClickListener = null;
    }
  }

  dt = definePreset(MyPreset, {
    root: {
      paddingX: 0,
      paddingY: 0,
    }
  });
  protected readonly initialSignupRoute = initialSignupRoute;
  protected readonly initialSignupRouteIncludingInfo = initialSignupRouteIncludingInfo;
}
